import { html, type TemplateResult } from "lit";
import type { PublicTrigger } from "../protocol";
import { cardLabel } from "./game-text";

// 603.3b: a player with two or more triggers to put on the stack at once chooses their order. As on
// Arena, they're dragged into it across the dimmed table, the one resolving last on the left; Done
// puts them on the stack left to right, one pick at a time (#184), each pick's index as ChoseMode.

export type TriggerOrdering = { question: PublicTrigger[]; order: number[]; dragging: number | null };

export type TriggerOrderHost = {
  triggerCard: (t: PublicTrigger) => TemplateResult;
  // Hovering a trigger previews its source.
  hover: (oid: number | null, e?: MouseEvent) => void;
  ordering: TriggerOrdering;
  setOrdering: (ordering: TriggerOrdering) => void;
  putOnStackInOrder: (order: number[]) => void;
  // The overlay's way out (its Cancel) and its button to look at the battlefield under it.
  foot: TemplateResult;
  peek: TemplateResult;
  peeking: boolean;
};

export function freshOrdering(triggers: PublicTrigger[]): TriggerOrdering {
  return { question: triggers, order: triggers.map((_, i) => i), dragging: null };
}

// Two of one source's triggered abilities are told apart by which ability each is.
function triggerLabel(all: PublicTrigger[], t: PublicTrigger): string {
  const label = `${cardLabel(t.source)} trigger`;
  const shared = all.some((o) => o !== t && cardLabel(o.source) === cardLabel(t.source));
  return shared ? `${label} (ability ${t.index + 1})` : label;
}

function movedTo(order: number[], from: number, to: number): number[] {
  const next = order.filter((_, k) => k !== from);
  next.splice(to, 0, order[from]!);
  return next;
}

function slotUnder(row: Element, x: number): number {
  const cards = [...row.querySelectorAll(":scope > .trigger-card")];
  const at = cards.findIndex((c) => { const r = c.getBoundingClientRect(); return x < r.left + r.width / 2; });
  return at === -1 ? cards.length - 1 : at;
}

export function renderTriggerOrder(host: TriggerOrderHost): TemplateResult {
  const { question: triggers, order, dragging } = host.ordering;
  const set = (next: Partial<TriggerOrdering>) => host.setOrdering({ ...host.ordering, ...next });
  const down = (k: number) => (e: PointerEvent) => {
    if (e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    set({ dragging: k });
  };
  const move = (e: PointerEvent) => {
    if (dragging === null) return;
    const row = (e.currentTarget as HTMLElement).parentElement;
    if (!row) return;
    const to = slotUnder(row, e.clientX);
    if (to !== dragging) set({ order: movedTo(order, dragging, to), dragging: to });
  };
  const up = () => { if (dragging !== null) set({ dragging: null }); };
  const nudge = (k: number, by: number) => set({ order: movedTo(order, k, k + by) });
  return html`<div class="pop prompt choose-one trigger-order ${dragging !== null ? "dragging" : ""} ${host.peeking ? "peeking" : ""}" role="dialog" aria-label="Order triggers" style="--n: ${order.length}">
    <header class="choose-heading">
      <h3>Order Triggers</h3>
      <div class="sub">Drag triggers to choose resolution order</div>
    </header>
    <div class="trigger-row-wrap">
      <span class="trigger-end">Last</span>
      <div class="choose-options trigger-row" role="list">
        ${order.map((i, k) => {
          const t = triggers[i]!;
          const label = triggerLabel(triggers, t);
          return html`<div class="trigger-card ${dragging === k ? "lifted" : ""}" role="listitem" aria-label=${label}
              @pointerdown=${down(k)} @pointermove=${move} @pointerup=${up} @pointercancel=${up}
              @mouseenter=${(e: MouseEvent) => host.hover(t.source.object_id, e)} @mouseleave=${() => host.hover(null)}>
            <span class="option-card">${host.triggerCard(t)}</span>
            <span class="trigger-nudges">
              <button class="btn" aria-label="Resolve ${label} later" ?disabled=${k === 0} @pointerdown=${(e: Event) => e.stopPropagation()} @click=${() => nudge(k, -1)}>‹</button>
              <span class="trigger-place">${order.length - k}</span>
              <button class="btn" aria-label="Resolve ${label} sooner" ?disabled=${k === order.length - 1} @pointerdown=${(e: Event) => e.stopPropagation()} @click=${() => nudge(k, 1)}>›</button>
            </span>
          </div>`;
        })}
      </div>
      <span class="trigger-end">First</span>
    </div>
    <div class="choose-actions">
      <div class="choose-buttons">
        ${host.peek}
        <button class="btn done" @click=${() => host.putOnStackInOrder(order)}>Done</button>
      </div>
      ${host.foot}
    </div>
  </div>`;
}
